import React, { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { PLATFORM_META } from "@/lib/meta";
import { Users, Plus, Pause, Play } from "lucide-react";

const PLATFORMS = ["x", "telegram", "reddit", "tiktok", "instagram", "youtube"];
const TIERS = ["micro", "mid", "macro"];

export default function RosterAdmin() {
  const [rows, setRows] = useState([]);
  const [isAdmin, setIsAdmin] = useState(true);
  const [form, setForm] = useState({ platform: "x", handle: "", tier: "micro", price_usd: 25 });
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => {
    api.get("/kol/admin/roster").then(({ data }) => setRows(data.roster || [])).catch((e) => { if (e?.response?.status === 403) setIsAdmin(false); });
    api.get("/kol/platforms").then(({ data }) => setIsAdmin(!!data.is_admin)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const add = async () => {
    if (!form.handle.trim()) return toast.error("Enter a handle.");
    setBusy(true);
    try { await api.post("/kol/roster", form); toast.success("KOL added"); setForm({ ...form, handle: "" }); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not add."); }
    finally { setBusy(false); }
  };
  const setStatus = async (id, status) => { try { await api.post(`/kol/roster/${id}/status`, { status }); load(); } catch { toast.error("Failed"); } };
  const setPrice = async (id, price_usd) => { try { await api.patch(`/kol/roster/${id}`, { price_usd }); toast.success("Price updated"); load(); } catch { toast.error("Failed"); } };

  if (!isAdmin) return <div className="mx-auto max-w-3xl px-5 py-16 text-center text-dust" data-testid="roster-admin-denied">Admin only. Ask an admin to add your wallet to ADMIN_WALLETS.</div>;

  const byPlatform = PLATFORMS.map((p) => [p, rows.filter((r) => r.platform === p)]).filter(([, r]) => r.length);

  return (
    <div className="mx-auto max-w-5xl px-5 py-8 lg:px-8" data-testid="roster-admin">
      <div className="mb-6 flex items-center gap-2">
        <Users size={22} className="text-pulp" />
        <div><h1 className="text-2xl font-bold tracking-tight">KOL roster</h1><p className="text-sm text-dust">Add, price and pause creators. Sliders unlock a platform once it has ≥1 active KOL.</p></div>
      </div>

      <div className="mb-6 rounded-2xl border border-line bg-surface p-4">
        <p className="mb-3 text-xs uppercase tracking-wider text-dust">Add KOL</p>
        <div className="flex flex-wrap items-end gap-2">
          <div>
            <label className="text-[10px] uppercase text-dust">Platform</label>
            <div className="mt-1 flex flex-wrap gap-1">
              {PLATFORMS.map((p) => (
                <button key={p} onClick={() => setForm({ ...form, platform: p })} data-testid={`add-plat-${p}`}
                  className={`rounded-lg border px-2 py-1 text-xs ${form.platform === p ? "border-juice bg-juice/10 text-cream" : "border-line text-dust"}`}>{PLATFORM_META[p]?.label}</button>
              ))}
            </div>
          </div>
          <div className="flex-1 min-w-[160px]"><label className="text-[10px] uppercase text-dust">Handle</label><Input value={form.handle} onChange={(e) => setForm({ ...form, handle: e.target.value })} placeholder="@handle or url" className="mt-1 bg-ink text-sm" data-testid="add-handle" /></div>
          <div>
            <label className="text-[10px] uppercase text-dust">Tier</label>
            <div className="mt-1 flex gap-1">
              {TIERS.map((t) => <button key={t} onClick={() => setForm({ ...form, tier: t })} data-testid={`add-tier-${t}`} className={`rounded-lg border px-2 py-1 text-xs capitalize ${form.tier === t ? "border-juice bg-juice/10 text-cream" : "border-line text-dust"}`}>{t}</button>)}
            </div>
          </div>
          <div className="w-24"><label className="text-[10px] uppercase text-dust">Price $</label><Input type="number" value={form.price_usd} onChange={(e) => setForm({ ...form, price_usd: e.target.value })} className="mt-1 bg-ink text-sm" data-testid="add-price" /></div>
          <Button disabled={busy} onClick={add} className="gap-1 bg-pulp text-ink hover:bg-pulp/90" data-testid="add-kol-btn"><Plus size={15} /> Add</Button>
        </div>
      </div>

      {byPlatform.map(([p, list]) => (
        <div key={p} className="mb-5">
          <p className="mb-2 flex items-center gap-2 text-sm font-semibold"><span className="h-2 w-2 rounded-full" style={{ background: PLATFORM_META[p]?.color }} /> {PLATFORM_META[p]?.label} <span className="text-xs font-normal text-dust">({list.filter((r) => r.status === "active").length} active)</span></p>
          <div className="overflow-hidden rounded-2xl border border-line bg-surface">
            {list.map((r) => (
              <div key={r.id} className="flex flex-wrap items-center justify-between gap-2 border-b border-line/60 px-4 py-3 text-sm last:border-0" data-testid={`roster-row-${r.id}`}>
                <div className="flex items-center gap-2">
                  <span className="font-medium text-cream">{r.display_name}</span>
                  <span className="font-mono text-xs text-dust">{r.handle}</span>
                  <span className="rounded-full border border-line px-2 py-0.5 text-[10px] capitalize text-dust">{r.tier}</span>
                  {r.status !== "active" && <span className="rounded-full border border-warn/30 bg-warn/10 px-2 py-0.5 text-[10px] text-warn">{r.status}</span>}
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-[10px] text-dust">{(r.avg_views || 0).toLocaleString()} views</span>
                  <Input type="number" defaultValue={r.price_usd} onBlur={(e) => Number(e.target.value) !== r.price_usd && setPrice(r.id, Number(e.target.value))} className="h-8 w-20 bg-ink font-mono text-xs" data-testid={`roster-price-${r.id}`} />
                  {r.status === "active" ? (
                    <Button size="sm" variant="outline" className="h-8 gap-1" onClick={() => setStatus(r.id, "paused")} data-testid={`roster-pause-${r.id}`}><Pause size={13} /> Pause</Button>
                  ) : (
                    <Button size="sm" className="h-8 gap-1 bg-pulp text-ink" onClick={() => setStatus(r.id, "active")} data-testid={`roster-activate-${r.id}`}><Play size={13} /> Activate</Button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}
